import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { Gallery } from '../types/Gallery';
import GalleryInfoDialog from '../components/Gallery/GalleryInfoDialog';
import ShareModal from '../components/Sharing/ShareModal';
import { generateShareContent } from '../utils/sharingUrls';
import SEOHead from '../components/SEO/SEOHead';
import { cloudinaryDeliveryUrl, cloudinaryLightbox, photographAlt } from '../utils/imageSeo';
import { useMobileViewer, usePhotographStage } from '../components/Gallery/usePhotographStage';
import BrandMarkSpinner from '../components/Loading/BrandMarkSpinner';
import sitePaths from '../utils/sitePaths';
import '../components/Gallery/OriginalGalleryView.css';
import './SharedGalleryPage.css';

const MODAL_PREVIEW_WIDTH = 240;

export function galleryCloseTarget(historyIndex: unknown): -1 | '/' {
  if (typeof historyIndex === 'number' && historyIndex > 0) return -1;
  return '/';
}

function toViewGallery(record: any): Gallery {
  const processedImages = record.images?.map((img: any, index: number) => {
    const imageUrl = typeof img === 'string'
      ? img
      : (img.url || img.secure_url || '/placeholder.jpg');

    return {
      publicId: img.publicId || img.public_id || `gallery_${record.id}_${index}`,
      url: imageUrl,
      alt: img.alt || img.filename || img.original_filename || `${record.name} - Image ${index + 1}`,
      width: img.width || 400,
      height: img.height || 300,
      filename: img.filename || img.original_filename || `image_${index + 1}`,
    };
  }) || [];

  const heroImage = processedImages.length > 0 ? processedImages[0] : {
    publicId: 'default_hero',
    url: '/placeholder.jpg',
    alt: 'Gallery Hero Image',
    width: 400,
    height: 300,
    filename: 'hero.jpg'
  };

  return {
    id: record.id,
    name: record.name,
    description: record.description || '',
    excerpt: record.excerpt || '',
    category: record.category || 'general',
    images: processedImages,
    heroImage,
    galleryType: record.galleryType || 'mosaic',
    createdAt: record.createdAt || new Date().toISOString(),
    updatedAt: record.updatedAt || new Date().toISOString(),
    published: record.published !== undefined ? record.published : true,
    heroImagePosition: record.heroImagePosition,
    heroImageZoom: record.heroImageZoom,
    heroImageCustomX: record.heroImageCustomX,
    heroImageCustomY: record.heroImageCustomY,
    heroImageCropBox: record.heroImageCropBox
  };
}

function galleryMatchesRoute(gallery: Gallery | null, routeId?: string): boolean {
  if (!gallery || !routeId) return false;
  const key = String(routeId);
  const slug = sitePaths.slugify(key);
  return String(gallery.id) === key || sitePaths.slugify(gallery.name || '') === slug;
}

function galleryFromBootstrap(id?: string): Gallery | null {
  if (typeof document === 'undefined' || !id) return null;
  const node = document.getElementById('page-bootstrap');
  if (!node?.textContent) return null;
  try {
    const data = JSON.parse(node.textContent);
    const record = data?.kind === 'gallery' ? data.gallery : null;
    if (!record) return null;
    const key = String(id);
    const slug = sitePaths.slugify(key);
    const matches = String(record.id) === key
      || String(record.slug || '') === key
      || sitePaths.slugify(record.slug || record.name || '') === slug;
    return matches ? toViewGallery(record) : null;
  } catch (error) {
    return null;
  }
}

const SharedGalleryPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const seededGallery = galleryFromBootstrap(id);
  const [gallery, setGallery] = useState<Gallery | null>(seededGallery);
  const [loading, setLoading] = useState(!seededGallery);
  const [error, setError] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharpReadyUrl, setSharpReadyUrl] = useState('');
  const [previewReadyUrl, setPreviewReadyUrl] = useState('');
  const [chromeHidden, setChromeHidden] = useState(false);
  const [framesOpen, setFramesOpen] = useState(false);
  const mobileViewer = useMobileViewer();
  const filmstripRef = useRef<HTMLDivElement | null>(null);
  const [trackedId, setTrackedId] = useState(id);

  if (trackedId !== id) {
    setTrackedId(id);
    setCurrentImageIndex(0);
    setChromeHidden(false);
    setFramesOpen(false);
    setIsInfoOpen(false);
    setShareOpen(false);
    setSharpReadyUrl('');
    setPreviewReadyUrl('');
    const seeded = galleryFromBootstrap(id);
    if (seeded) {
      setGallery(seeded);
      setError(null);
      setLoading(false);
    } else if (!galleryMatchesRoute(gallery, id)) {
      setError(null);
      setLoading(true);
    }
  }

  useEffect(() => {
    let cancelled = false;
    const seeded = galleryFromBootstrap(id);
    if (seeded) {
      setGallery(seeded);
      setError(null);
      setLoading(false);
    }
    const loadGallery = async () => {
      console.log('🔗 SharedGalleryPage: Loading gallery with ID:', id);
      
      if (!id) {
        console.log('❌ SharedGalleryPage: No gallery ID provided');
        setError('No gallery ID provided');
        setLoading(false);
        return;
      }

      try {
        const { getGalleryById, loadLiveGalleries } = await import('../utils/GalleryAPI');
        let foundGallery = await getGalleryById(id).catch(() => null);
        const slug = sitePaths.slugify(id);
        const directMatches = foundGallery && (
          String(foundGallery.id) === String(id)
          || sitePaths.slugify(foundGallery.name) === slug
        );
        if (!directMatches) foundGallery = null;
        if (!foundGallery) {
          const stamped = sitePaths.withGallerySlugs(await loadLiveGalleries('cards').catch(() => []));
          const match = sitePaths.findGalleryByKey(stamped, id);
          if (match) foundGallery = await getGalleryById(String(match.id)).catch(() => match);
        }
        if (!foundGallery) {
          const stored = localStorage.getItem('admin_galleries');
          if (stored) {
            foundGallery = sitePaths.findGalleryByKey(JSON.parse(stored), id);
          }
        }

        if (cancelled) return;

        if (foundGallery) {
            const record = foundGallery;
            const canonical = sitePaths.galleryPath(sitePaths.withGallerySlugs([record])[0]);
            if (canonical && canonical !== `/gallery/${id}`) {
              navigate(canonical, { replace: true });
            }
            if (cancelled) return;
            const processedGallery = toViewGallery(record);
            
            console.log('🔗 SharedGalleryPage: Gallery with positioning:', {
              name: processedGallery.name,
              heroImagePosition: processedGallery.heroImagePosition,
              heroImageZoom: processedGallery.heroImageZoom,
              heroImageCropBox: processedGallery.heroImageCropBox
            });
            
            console.log('✅ SharedGalleryPage: Gallery processed successfully');
            
            setGallery(processedGallery);
            setError(null);
            setLoading(false);
            return;
        }

        if (cancelled) return;
        if (seeded) {
          setGallery(seeded);
          setError(null);
          setLoading(false);
          return;
        }
        
        // Gallery not found
        console.log('❌ SharedGalleryPage: Gallery not found in any source');
        setError('Gallery not found');
        setLoading(false);
      } catch (error) {
        console.error('Error loading gallery:', error);
        if (cancelled) return;
        if (seeded) {
          setGallery(seeded);
          setError(null);
          setLoading(false);
          return;
        }
        setError('Failed to load gallery');
        setLoading(false);
      }
    };

    loadGallery();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const handleClose = () => {
    const destination = galleryCloseTarget(window.history.state?.idx);
    if (destination === -1) {
      navigate(-1);
      return;
    }
    navigate(destination);
  };

  // Slideshow navigation functions
  const getCurrentImage = () => {
    if (!gallery || !gallery.images || gallery.images.length === 0) return null;
    return gallery.images[currentImageIndex];
  };

  const nextImage = () => {
    if (!gallery || !gallery.images) return;
    setCurrentImageIndex((prev) => (prev + 1) % gallery.images.length);
  };

  const prevImage = () => {
    if (!gallery || !gallery.images) return;
    setCurrentImageIndex((prev) => (prev - 1 + gallery.images.length) % gallery.images.length);
  };

  const stageGesture = usePhotographStage({
    onAdvance: nextImage,
    onNext: nextImage,
    onPrevious: prevImage,
    onToggleChrome: () => setChromeHidden((hidden) => !hidden),
  });

  useEffect(() => {
    if (!gallery || gallery.images.length < 2) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (isInfoOpen || shareOpen) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        nextImage();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        prevImage();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [gallery, isInfoOpen, shareOpen, currentImageIndex]);

  if (loading) {
    return (
      <div className="brand-mark-screen">
        <BrandMarkSpinner size="large" label="Loading gallery" caption />
      </div>
    );
  }

  if (error || !gallery) {
    console.log('🔗 SharedGalleryPage: Error or no gallery:', { error, gallery: !!gallery });
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '1.2rem',
        color: '#666',
        textAlign: 'center',
        padding: '2rem'
      }}>
        <h1>Gallery Not Found</h1>
        <p>{error || 'The requested gallery could not be found.'}</p>
        <button 
          onClick={() => navigate('/')}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#8B4513',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Go to Homepage
        </button>
      </div>
    );
  }

  console.log('🔗 SharedGalleryPage: Rendering gallery slideshow for:', gallery.name);
  
  const currentImage = getCurrentImage();
  const shareContent = gallery ? generateShareContent('gallery', gallery) : null;
  
  // Add cache-busting parameter to image URL for social sharing
  const imageUrlWithCacheBuster = shareContent && gallery ? (() => {
    const cacheBuster = gallery.updatedAt ? new Date(gallery.updatedAt).getTime() : Date.now();
    return shareContent.image.includes('?') 
      ? `${shareContent.image}&v=${cacheBuster}`
      : `${shareContent.image}?v=${cacheBuster}`;
  })() : '';

  const excerptText = (gallery.excerpt || '').trim();
  const fullDescriptionText = (gallery.description || '').trim();
  const hasInfoContent = Boolean(fullDescriptionText || gallery.category);
  const hasMultipleImages = gallery.images.length > 1;
  const currentRawUrl = currentImage?.url || currentImage?.secure_url || '';
  const previewUrl = currentRawUrl ? cloudinaryDeliveryUrl(currentRawUrl, MODAL_PREVIEW_WIDTH) : '';
  const sharp = currentRawUrl ? cloudinaryLightbox(currentRawUrl) : null;
  const sharpUrl = sharp ? sharp.src : '';
    const photographPending = Boolean(
      (previewUrl || sharpUrl)
      && previewReadyUrl !== previewUrl
      && sharpReadyUrl !== sharpUrl
    );
    const gallerySwitching = previewReadyUrl === '' && sharpReadyUrl === '' && Boolean(previewUrl || sharpUrl);
  const slideAspect = currentImage?.width && currentImage?.height
    ? currentImage.width / currentImage.height
    : 1.5;

  const showFrames = !mobileViewer || framesOpen;
  const lightbox = createPortal(
    <div
      className={`gallery-modal-overlay gallery-lightbox${chromeHidden ? ' chrome-hidden' : ''}${framesOpen ? ' frames-open' : ''}`}
      onClick={handleClose}
    >
      <div
        className="gallery-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gallery-lightbox-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="gallery-header">
          <div className="gallery-title-section">
            <h3 id="gallery-lightbox-title">{gallery.name}</h3>
            {sitePaths.galleryCanonicalPath(gallery) !== sitePaths.galleryPath(gallery) && (
              <p className="gallery-packages-link">
                <a href={sitePaths.galleryCanonicalPath(gallery)}>Family photography packages, prices and FAQs</a>
              </p>
            )}
            {(excerptText || hasInfoContent) && (
              <div className="gallery-description-row">
                {excerptText && <p className="gallery-description">{excerptText}</p>}
                {hasInfoContent && (
                  <button
                    className="gallery-more-info-btn"
                    type="button"
                    aria-expanded={isInfoOpen}
                    aria-controls="gallery-info-dialog"
                    onClick={() => setIsInfoOpen(true)}
                  >
                    <i className="fas fa-info-circle" aria-hidden="true"></i>
                    <span>More Info</span>
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="gallery-header-actions">
            {shareContent && (
              <button className="gallery-share-quiet" type="button" aria-label="Share gallery" onClick={() => setShareOpen(true)}>
                <i className="fas fa-share-alt" aria-hidden="true"></i>
                <span>Share</span>
              </button>
            )}
            <button className="gallery-lightbox-close" type="button" onClick={handleClose} aria-label="Close gallery">
              <i className="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <div className="slideshow-container">
          <div className={`slideshow-wrapper${hasMultipleImages ? ' has-nav' : ''}`}>
            {hasMultipleImages && (
              <button type="button" className="nav-arrow-btn nav-arrow-left" aria-label="Previous photograph" onClick={prevImage}>
                <i className="fas fa-chevron-left" aria-hidden="true"></i>
              </button>
            )}
            <div
              className="slideshow-main"
              onPointerDown={stageGesture.onPointerDown}
              onPointerUp={stageGesture.onPointerUp}
              onPointerCancel={stageGesture.onPointerCancel}
              onClick={stageGesture.onClick}
            >
              {currentImage && (
                <div className="slideshow-stage" style={{ '--slide-aspect': String(slideAspect) } as React.CSSProperties}>
                  {gallerySwitching ? (
                    <div className="brand-mark-overlay is-blocking">
                      <BrandMarkSpinner size="large" label="Loading gallery" caption />
                    </div>
                  ) : photographPending && (
                    <div className="brand-mark-overlay">
                      <BrandMarkSpinner size="small" tone="dark" label="Loading photograph" />
                    </div>
                  )}
                  {previewUrl && (
                    <img
                      src={previewUrl}
                      alt=""
                      aria-hidden="true"
                      className={`slideshow-img slideshow-img-preview${gallerySwitching ? ' is-hidden-while-switching' : ''}`}
                      decoding="sync"
                      onLoad={() => setPreviewReadyUrl(previewUrl)}
                    />
                  )}
                  {sharpUrl && (
                    <img
                      src={sharpUrl}
                      srcSet={sharp && sharp.srcSet ? sharp.srcSet : undefined}
                      sizes="100vw"
                      alt={photographAlt(gallery.name, currentImage, { hero: currentImageIndex === 0, description: gallery.excerpt || gallery.description })}
                      className={`slideshow-img slideshow-img-sharp${gallerySwitching ? ' is-hidden-while-switching' : ''}${sharpReadyUrl === sharpUrl ? ' is-ready' : ''}`}
                      width={currentImage.width}
                      height={currentImage.height}
                      decoding="async"
                      onLoad={() => setSharpReadyUrl(sharpUrl)}
                    />
                  )}
                </div>
              )}
            </div>
            {hasMultipleImages && (
              <button type="button" className="nav-arrow-btn nav-arrow-right" aria-label="Next photograph" onClick={nextImage}>
                <i className="fas fa-chevron-right" aria-hidden="true"></i>
              </button>
            )}
          </div>
          <button
            type="button"
            className="gallery-frames-toggle"
            aria-expanded={framesOpen}
            onClick={(event) => {
              event.stopPropagation();
              setFramesOpen((open) => !open);
            }}
          >
            {framesOpen ? 'Hide photographs' : 'Photographs'}
          </button>
          <p className="gallery-lightbox-counter" aria-live="polite">
            {currentImageIndex + 1} / {gallery.images.length}
          </p>
          {showFrames && <div className="gallery-filmstrip" role="list" ref={filmstripRef}>
            {gallery.images.map((image, index) => (
              <button
                key={image.publicId || index}
                className={`gallery-filmstrip-item ${index === currentImageIndex ? 'active' : ''}`}
                onClick={() => setCurrentImageIndex(index)}
                type="button"
                aria-label={`Go to image ${index + 1}`}
                aria-current={index === currentImageIndex ? 'true' : undefined}
                role="listitem"
              >
                <img
                  src={cloudinaryDeliveryUrl(image.url || image.secure_url || '', 240)}
                  alt={photographAlt(gallery.name, image)}
                  loading="lazy"
                  decoding="async"
                />
              </button>
            ))}
          </div>}
        </div>

        {isInfoOpen && (
          <GalleryInfoDialog
            name={gallery.name}
            category={gallery.category}
            excerpt={excerptText}
            description={fullDescriptionText}
            onClose={() => setIsInfoOpen(false)}
          />
        )}
        {shareContent && (
          <ShareModal isOpen={shareOpen} onClose={() => setShareOpen(false)} content={shareContent} onShare={() => {}} />
        )}
      </div>
    </div>,
    document.body
  );
  
  return (
    <div className="shared-gallery-page">
      {gallery && shareContent && (
        <SEOHead
          exactTitle
          title={sitePaths.galleryDisplayTitle(gallery) || shareContent.title}
          description={shareContent.description}
          image={imageUrlWithCacheBuster}
          type="article"
          publishedTime={gallery.createdAt}
          modifiedTime={gallery.updatedAt}
          canonicalUrl={`https://carmenlorraine.com${sitePaths.galleryCanonicalPath(gallery)}`}
          keywords={[gallery.category, gallery.name, 'photography gallery', 'photo gallery']}
        />
      )}
      <h1 className="visually-hidden">{gallery.name.trim()}</h1>
      {lightbox}
    </div>
  );
};

export default SharedGalleryPage;
